/**
 * @fileoverview Contact Logs Modal
 * @module components/modals/ContactLogsModal
 * @description Read-only list of every distinct contact attempt logged
 * against an escalation, built from that escalation's current record (the
 * member's /escalations list entry) plus every prior version returned by
 * /escalations/{id}/history - contactAt/contactMethod/contactParty are flat
 * fields on each snapshot, so a "contact attempt" is just a snapshot whose
 * contactAt is set. Multiple versions can share the same contactAt when a
 * version was re-saved without changing the contact info, so entries are
 * de-duplicated by contactAt before display. Each attempt renders as a row
 * under three columns: Contact Party, Contact Method, Contact At.
 */

import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// contactAt is stored as a plain "YYYY-MM-DDTHH:mm" wall-clock string (see
// combineDateTime in CreateEscalationModal) - not a UTC timestamp - so it's
// formatted directly rather than through formatDateTime, which assumes UTC
// and would shift it by the viewer's offset.
const formatContactAt = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const columnsGridSx = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  columnGap: 2,
};

const columnHeaderSx = {
  fontSize: "11px",
  fontWeight: 700,
  textTransform: "uppercase",
  color: "#7F7C9F",
};

const ContactLogsModal = ({ open, onClose, contactLogs = [] }) => {
  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      keepMounted={false}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            padding: { xs: "16px", md: "24px" },
            backgroundColor: "#F7F8FB",
            boxShadow: "none",
            border: "none",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          m: 0,
          width: "100%",
          height: "40px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            lineHeight: "30px",
            color: "#000000",
          }}
        >
          Contact Logs
        </Typography>
        <IconButton onClick={onClose} aria-label="Close contact logs dialog">
          <CloseIcon sx={{ color: "#7F7C9F" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: 0, mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            maxHeight: "60vh",
            overflowY: "auto",
            pr: 1,
          }}
        >
          {contactLogs.length === 0 ? (
            <Box
              sx={{
                backgroundColor: "#fff",
                border: "1px solid #E8E8E9",
                borderRadius: "8px",
                padding: 3,
                textAlign: "center",
                color: "#7F7C9F",
                fontSize: "14px",
              }}
            >
              No contact attempts logged yet.
            </Box>
          ) : (
            <>
              <Box sx={{ ...columnsGridSx, padding: "0 16px" }}>
                <Typography sx={columnHeaderSx}>Contact Party</Typography>
                <Typography sx={columnHeaderSx}>Contact Method</Typography>
                <Typography sx={columnHeaderSx}>Contact At</Typography>
              </Box>
              {contactLogs.map((log, index) => (
                <Box
                  key={`${log.contactAt ?? "log"}-${index}`}
                  sx={{
                    ...columnsGridSx,
                    alignItems: "center",
                    border: "1px solid #E8E8E9",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    padding: "10px 16px",
                    fontSize: "14px",
                    color: "#1B2F3F",
                  }}
                >
                  <Box sx={{ wordBreak: "break-word" }}>
                    {log.contactParty || "-"}
                  </Box>
                  <Box sx={{ wordBreak: "break-word" }}>
                    {log.contactMethod || "-"}
                  </Box>
                  <Box sx={{ wordBreak: "break-word" }}>
                    {formatContactAt(log.contactAt) || "-"}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

ContactLogsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  contactLogs: PropTypes.arrayOf(
    PropTypes.shape({
      contactMethod: PropTypes.string,
      contactAt: PropTypes.string,
      contactParty: PropTypes.string,
    }),
  ),
};

export default ContactLogsModal;
